import { useEffect, useRef, useState } from "react";

const FALLBACK_DELAY = 1400;

/**
 * True once the element has reached the reveal line, including when it is
 * already above the viewport: a one-shot entrance that was jumped past by an
 * anchor link or a restored scroll position must resolve to "shown", not stay
 * at opacity 0 forever.
 */
function hasReachedRevealLine(el: Element, bottomMargin: number) {
  const r = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  return r.top < vh - bottomMargin;
}

/**
 * Adds the `in` class once the element has entered the viewport, then stops
 * observing. Pairs with the `.reveal` / `.line-mask` classes in index.css,
 * both of which resolve to their final state under prefers-reduced-motion.
 *
 * IntersectionObserver delivery is suspended while a document is hidden, and
 * some embedded/automated contexts never resume it. Because every revealed
 * element starts at opacity 0, a silent observer would leave the whole page
 * blank, so a geometry fallback takes over if the observer never reports.
 */
export function useInView<T extends HTMLElement = HTMLDivElement>(
  options: { threshold?: number; rootMargin?: string; once?: boolean } = {}
) {
  const { threshold = 0.15, rootMargin = "0px 0px -8% 0px", once = true } = options;
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    let delivered = false;
    let disposed = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        delivered = true;
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold, rootMargin }
    );
    observer.observe(el);

    // Fallback: if the observer has said nothing at all by now, drive the
    // reveal from geometry instead so content is never stranded at opacity 0.
    let detach: (() => void) | undefined;

    const timer = setTimeout(() => {
      if (delivered || disposed) return;

      const check = () => {
        if (disposed) return;
        if (hasReachedRevealLine(el, window.innerHeight * 0.08)) {
          setInView(true);
          if (once) detach?.();
        }
      };

      window.addEventListener("scroll", check, { passive: true });
      window.addEventListener("resize", check, { passive: true });
      document.addEventListener("visibilitychange", check);
      detach = () => {
        window.removeEventListener("scroll", check);
        window.removeEventListener("resize", check);
        document.removeEventListener("visibilitychange", check);
      };
      check();
    }, FALLBACK_DELAY);

    return () => {
      disposed = true;
      clearTimeout(timer);
      observer.disconnect();
      detach?.();
    };
  }, [threshold, rootMargin, once]);

  return { ref, inView } as const;
}

export function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
